/**
 * VoiceManager.js
 * Manages spatial voice chat. Captures microphone input
 * and spatializes remote peer audio streams using Web Audio API HRTF PannerNodes.
 */
export class VoiceManager {
  constructor() {
    this.audioContext = null;
    this.localStream = null;
    this.isMuted = true;
    this.isEnabled = false;
    this.remotePeers = new Map(); // peerId -> { sourceNode, pannerNode, audioEl }
    this.THREE = null;
  }

  /**
   * Initializes the local spatial audio context.
   * Must be called in response to a user interaction to satisfy browser security.
   * @param {THREE} THREE - Reference to THREE.js to compile mathematical operations
   */
  async init(THREE) {
    this.THREE = THREE;
    if (this.audioContext) return;

    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      console.log('[VoiceManager] Web Audio Spatial Context initialized.');
    } catch (err) {
      console.error('[VoiceManager] Failed to create AudioContext:', err);
    }
  }

  /**
   * Starts local microphone capture.
   */
  async startMicrophone() {
    if (this.localStream) return this.localStream;

    try {
      console.log('[VoiceManager] Requesting microphone access...');
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      console.log('[VoiceManager] Microphone capture started.');
      
      // Sync initial mute state to the audio tracks
      this.setMute(this.isMuted);
      return this.localStream;
    } catch (err) {
      console.warn('[VoiceManager] Microphone access denied or unavailable:', err);
      this.localStream = null;
      throw err;
    }
  }

  /**
   * Stops local microphone capture.
   */
  stopMicrophone() {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
      console.log('[VoiceManager] Microphone capture stopped.');
    }
  }

  /**
   * Toggles microphone mute.
   * @param {boolean} mute - Mute state
   */
  setMute(mute) {
    this.isMuted = mute;
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = !mute;
      });
    }
    console.log(`[VoiceManager] Local microphone set to: ${mute ? 'MUTED' : 'TALKING'}`);
  }

  /**
   * Links a remote WebRTC audio stream to a 3D spatial PannerNode.
   * @param {string} peerId - Unique peer identifier
   * @param {MediaStream} stream - Received RTC audio stream
   */
  addPeerStream(peerId, stream) {
    if (!this.audioContext) {
      console.warn('[VoiceManager] AudioContext not initialized yet.');
      return;
    }

    this.removePeerStream(peerId);

    console.log(`[VoiceManager] Attaching audio stream for peer: ${peerId}`);

    try {
      // Safari/iOS WebRTC voice chat bypass trick:
      // MediaStream elements in WebRTC can remain silent in Web Audio unless played by a visible or hidden media element first.
      const audioEl = document.createElement('audio');
      audioEl.srcObject = stream;
      audioEl.autoplay = true;
      audioEl.style.display = 'none';
      document.body.appendChild(audioEl);
      audioEl.play().catch(e => console.warn('[VoiceManager] Auto-play wake-up failed:', e));

      // Resume context if suspended
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      // Create spatial nodes using createMediaElementSource to solve the Chrome multiple stream bug
      const sourceNode = this.audioContext.createMediaElementSource(audioEl);
      const pannerNode = this.audioContext.createPanner();

      // Configure spatial settings
      pannerNode.panningModel = 'HRTF'; // Premium High Resolution spatial synthesis
      pannerNode.distanceModel = 'inverse'; // Inverse physical falloff model
      pannerNode.refDistance = 1.2; // Distance where sound begins to decay
      pannerNode.maxDistance = 18; // Beyond 18m, sound decays entirely
      pannerNode.rolloffFactor = 2.0; // Rapid decay to create hallway isolation

      // Connect graph
      sourceNode.connect(pannerNode);
      pannerNode.connect(this.audioContext.destination);

      this.remotePeers.set(peerId, { sourceNode, pannerNode, audioEl });
      console.log(`[VoiceManager] Spatial audio node mapped successfully for peer: ${peerId}`);
    } catch (error) {
      console.error('[VoiceManager] Error binding peer stream:', error);
    }
  }

  /**
   * Removes and disposes remote peer spatial nodes.
   * @param {string} peerId - Unique peer identifier
   */
  removePeerStream(peerId) {
    const peerData = this.remotePeers.get(peerId);
    if (!peerData) return;

    console.log(`[VoiceManager] Cleaning spatial stream for peer: ${peerId}`);
    try {
      if (peerData.sourceNode) peerData.sourceNode.disconnect();
      if (peerData.pannerNode) peerData.pannerNode.disconnect();
      if (peerData.audioEl) {
        peerData.audioEl.pause();
        peerData.audioEl.remove();
      }
    } catch (e) {
      console.warn('[VoiceManager] Error disconnecting spatial nodes:', e);
    }

    this.remotePeers.delete(peerId);
  }

  /**
   * Updates the WebAudio listener position/orientation and all remote panners.
   * @param {THREE.Camera} camera - Local three.js camera
   * @param {Map} remotePeersPositions - Map of peerId -> THREE.Vector3 absolute positions
   */
  update(camera, remotePeersPositions) {
    if (!this.audioContext || this.audioContext.state === 'suspended') return;

    const listener = this.audioContext.listener;
    const time = this.audioContext.currentTime;

    // 1. Update the WebAudio listener relative to local camera position
    const camPos = camera.position;
    this.safeSetPosition(listener, camPos.x, camPos.y, camPos.z, time);

    // Get camera looking direction vectors
    const forward = new this.THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    const up = new this.THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
    this.safeSetOrientation(listener, forward.x, forward.y, forward.z, up.x, up.y, up.z, time);

    // 2. Update each remote player's spatial panner node
    for (const [peerId, peerData] of this.remotePeers.entries()) {
      const peerPos = remotePeersPositions.get(peerId);
      if (peerPos && peerData.pannerNode) {
        this.safeSetPosition(peerData.pannerNode, peerPos.x, peerPos.y, peerPos.z, time);
      }
    }
  }

  /**
   * Standardized backward-compatible positioning helper.
   */
  safeSetPosition(nodeOrListener, x, y, z, time) {
    if (nodeOrListener.positionX) {
      nodeOrListener.positionX.setValueAtTime(x, time);
      nodeOrListener.positionY.setValueAtTime(y, time);
      nodeOrListener.positionZ.setValueAtTime(z, time);
    } else if (nodeOrListener.setPosition) {
      nodeOrListener.setPosition(x, y, z);
    }
  }

  /**
   * Standardized backward-compatible orientation helper.
   */
  safeSetOrientation(listener, fx, fy, fz, ux, uy, uz, time) {
    if (listener.forwardX) {
      listener.forwardX.setValueAtTime(fx, time);
      listener.forwardY.setValueAtTime(fy, time);
      listener.forwardZ.setValueAtTime(fz, time);
      listener.upX.setValueAtTime(ux, time);
      listener.upY.setValueAtTime(uy, time);
      listener.upZ.setValueAtTime(uz, time);
    } else if (listener.setOrientation) {
      listener.setOrientation(fx, fy, fz, ux, uy, uz);
    }
  }

  /**
   * Resets and cleans all connections.
   */
  destroy() {
    this.stopMicrophone();

    for (const peerId of this.remotePeers.keys()) {
      this.removePeerStream(peerId);
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    console.log('[VoiceManager] Voice systems destroyed.');
  }
}
